import { Button, Dialog, notify, SearchInput, SegmentedControl, StatusPill } from "@flexfreight/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { AnthropicCard, M365Card, ZohoBooksCard, ZohoConnectionCard, ZohoDeskCard } from "./Integrations";
import { GoogleMapsCard } from "./GoogleMapsCard";
import { EagleLoadCard, ResendCard, SigninPolicyCard, TwilioCard } from "./SigninProviders";
import { AdminFrame } from "./AdminFrame";
import { IntegrationGuide } from "./IntegrationGuide";
import { COMING_SOON, INTEGRATION_CATEGORIES, INTEGRATIONS, tileStatus, type IntegrationCategory, type IntegrationId, type TileStatus } from "./settingsCatalog";

type Filter = "all" | "connected" | "not-set-up" | "soon";
const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: "all", label: "All" },
  { value: "connected", label: "Connected" },
  { value: "not-set-up", label: "Not set up" },
  { value: "soon", label: "Coming soon" },
];
const CAT_TONE: Record<IntegrationCategory, number> = { Zoho: 1, Support: 1, Billing: 2, "Email & text": 2, AI: 3, Carriers: 4, Maps: 5, Security: 6 };

/**
 * Admin Center > Integrations: a catalog of tiles (search, category sub-nav, status filter). A tile opens that
 * integration's own settings in a pop-up; ?open=<id> deep-links straight to it, ?cat=<category> narrows the list.
 */
export function IntegrationsPage() {
  const me = useConsoleMe();
  const canManage = me.capabilities.includes("integrations:manage");
  const [params, setParams] = useSearchParams();
  const open = params.get("open") as IntegrationId | null;
  const cat = INTEGRATION_CATEGORIES.find((c) => c === params.get("cat")) ?? null;
  const filter = (FILTERS.find((f) => f.value === params.get("show"))?.value ?? "all") as Filter;
  const term = params.get("q") ?? "";
  const setParam = (k: string, v: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (v) n.set(k, v);
        else n.delete(k);
        return n;
      },
      { replace: true },
    );

  const results = useQueries({ queries: INTEGRATIONS.map((i) => ({ queryKey: [...i.queryKey], queryFn: () => api<unknown>(i.url) })) });
  const UNAVAILABLE: TileStatus = { label: "Unavailable", tone: "neutral", state: "not-set-up" };
  const status = new Map<IntegrationId, TileStatus | null>(INTEGRATIONS.map((i, n) => [i.id, results[n]?.isError && !results[n]?.data ? UNAVAILABLE : tileStatus(i.id, results[n]?.data)]));

  const t = term.trim().toLowerCase();
  const matches = (name: string, blurb: string, category: IntegrationCategory, keywords = "") => (!cat || category === cat) && (!t || `${name} ${blurb} ${category} ${keywords}`.toLowerCase().includes(t));
  const tiles = filter === "soon" ? [] : INTEGRATIONS.filter((i) => matches(i.name, i.blurb, i.category, i.keywords) && (filter === "all" || (filter === "connected" ? status.get(i.id)?.state === "connected" : status.get(i.id)?.state !== "connected")));
  const soon = filter === "all" || filter === "soon" ? COMING_SOON.filter((c) => matches(c.name, c.blurb, c.category)) : [];
  const active = INTEGRATIONS.find((i) => i.id === open);

  return (
    <section className="ffa-page ffa-settings">
      <AdminFrame sub={cat ?? "All integrations"}>
        <div className="ffa-intmain">
          <header>
            <h1 className="ffa-page__title ffa-page__title--sub">Integrations</h1>
            <p className="ffa-head__sub">Connections to other systems. Keys are stored encrypted on the server and never shown again.</p>
          </header>
          <div className="ffa-inttools">
            <SearchInput label="Search integrations" placeholder="Search integrations" value={term} onChange={(v) => setParam("q", v || null)} />
            <SegmentedControl label="Show" options={FILTERS} value={filter} onChange={(v) => setParam("show", v === "all" ? null : v)} />
          </div>
          {tiles.length || soon.length ? (
            <ul className="ffa-intgrid">
              {tiles.map((i) => {
                const s = status.get(i.id);
                return (
                  <li key={i.id}>
                    <button type="button" className="ffa-inttile" onClick={() => setParam("open", i.id)} aria-label={`${i.name}: ${s?.label ?? "loading"}`}>
                      <Mark text={i.mark} tone={CAT_TONE[i.category]} id={i.id} />
                      <span className="ffa-inttile__name">{i.name}</span>
                      <span className="ffa-inttile__blurb">{i.blurb}</span>
                      <span className="ffa-inttile__foot">
                        <span className="ffa-inttile__cat">{i.category}</span>
                        {s ? <StatusPill tone={s.tone}>{s.label}</StatusPill> : <span className="ffa-muted ffa-small">Checking...</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
              {soon.map((c) => (
                <li key={c.name}>
                  <div className="ffa-inttile is-soon">
                    <Mark text={c.mark} tone={CAT_TONE[c.category]} />
                    <span className="ffa-inttile__name">{c.name}</span>
                    <span className="ffa-inttile__blurb">{c.blurb}</span>
                    <span className="ffa-inttile__foot">
                      <span className="ffa-inttile__cat">{c.category}</span>
                      <StatusPill tone="neutral">Coming soon</StatusPill>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ffa-muted">No integration matches.</p>
          )}
        </div>
      </AdminFrame>
      {active ? (
        <Dialog open size="lg" onClose={() => setParam("open", null)} title={active.name} description={active.blurb}>
          <div className={`ffa-intdlg${active.id === "zoho-desk" || active.id === "zoho-books" ? " ffa-intdlg--multi" : ""}`}>
            <IntegrationGuide id={active.id} defaultOpen={status.get(active.id)?.state !== "connected"} />
            <IntegrationLogo id={active.id} name={active.name} canManage={canManage} />
            {cardFor(active.id, canManage, me.capabilities.includes("carriers:go_live"))}
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

type LogoList = { logos: { id: string; version: number }[] };
const LOGOS_KEY = ["console", "integration-logos"] as const;
const useLogos = () => useQuery({ queryKey: LOGOS_KEY, queryFn: () => api<LogoList>("/v1/console/integration-logos"), staleTime: 60_000 });

/**
 * The integration's official logo: the one staff uploaded (in its pop-up), else a file shipped in
 * apps/console/public/integrations/<id>.png, else a clean monogram. Logos are never redrawn here.
 */
function Mark({ text, tone, id }: { text: string; tone: number; id?: string }) {
  const logos = useLogos();
  const [failed, setFailed] = useState<string | null>(null);
  const up = id ? logos.data?.logos.find((l) => l.id === id) : undefined;
  const src = up ? `/v1/console/integration-logos/${id}?v=${up.version}` : id ? `/integrations/${id}.png` : null;
  if (src && failed !== src) {
    return (
      <span className="ffa-inttile__mark ffa-inttile__mark--logo" aria-hidden="true">
        <img src={src} alt="" loading="lazy" onError={() => setFailed(src)} />
      </span>
    );
  }
  return <span className="ffa-inttile__mark" data-tone={tone} aria-hidden="true">{text}</span>;
}

const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
function base64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = "";
  for (let i = 0; i < bytes.length; i += 0x8000) out += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(out);
}

/** In each pop-up: the tile's logo, with Upload / Replace / Remove for staff who manage integrations. */
function IntegrationLogo({ id, name, canManage }: { id: IntegrationId; name: string; canManage: boolean }) {
  const qc = useQueryClient();
  const logos = useLogos();
  const input = useRef<HTMLInputElement>(null);
  const has = !!logos.data?.logos.some((l) => l.id === id);
  const done = () => void qc.invalidateQueries({ queryKey: LOGOS_KEY });
  const upload = useMutation({
    mutationFn: async (f: File) => {
      if (!LOGO_TYPES.includes(f.type)) throw new Error("Use a PNG, JPEG or WebP file.");
      if (f.size > 512 * 1024) throw new Error("Keep the file under 512 KB.");
      return api(`/v1/console/integration-logos/${id}`, { method: "PUT", body: { contentType: f.type, dataBase64: base64(await f.arrayBuffer()) } });
    },
    onSuccess: () => {
      done();
      notify.success(`${name} logo saved`, "It shows on the tile now.");
    },
    onError: (e) => notify.error("Logo not saved", e instanceof Error ? e.message : undefined),
  });
  const remove = useMutation({
    mutationFn: () => api(`/v1/console/integration-logos/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      done();
      notify.success(`${name} logo removed`);
    },
    onError: (e) => notify.error("Logo not removed", e instanceof Error ? e.message : undefined),
  });
  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) upload.mutate(f);
  };
  return (
    <div className="ffa-intlogo">
      <Mark text={INTEGRATIONS.find((i) => i.id === id)?.mark ?? ""} tone={0} id={id} />
      <div className="ffa-intlogo__text">
        <span className="ffa-intlogo__title">Logo</span>
        <span className="ffa-muted ffa-small">{has ? "The official logo shows on the tile." : "Upload the vendor's official logo (PNG, JPEG or WebP, up to 512 KB) from their brand or press page."}</span>
      </div>
      {canManage ? (
        <div className="ffa-intlogo__actions">
          <input ref={input} type="file" accept={LOGO_TYPES.join(",")} hidden onChange={onFile} aria-label={`${name} logo file`} />
          <Button size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>{has ? "Replace logo" : "Upload logo"}</Button>
          {has ? <Button size="sm" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button> : null}
        </div>
      ) : null}
    </div>
  );
}

function cardFor(id: IntegrationId, canManage: boolean, canGoLive: boolean): ReactNode {
  switch (id) {
    case "zoho": return <ZohoConnectionCard canManage={canManage} />;
    // Desk and Books need the shared Zoho sign-in: show it first, in the same window, with its keys and code.
    case "zoho-desk": return <><ZohoConnectionCard canManage={canManage} /><ZohoDeskCard canManage={canManage} /></>;
    case "zoho-books": return <><ZohoConnectionCard canManage={canManage} /><ZohoBooksCard canManage={canManage} /></>;
    case "m365": return <M365Card canManage={canManage} />;
    case "resend": return <ResendCard canManage={canManage} />;
    case "twilio": return <TwilioCard canManage={canManage} />;
    case "anthropic": return <AnthropicCard canManage={canManage} />;
    case "google-maps": return <GoogleMapsCard canManage={canManage} />;
    case "eagleload": return <EagleLoadCard canManage={canManage} canGoLive={canGoLive} />;
    case "signin-policy": return <SigninPolicyCard />;
  }
}
